import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sparkles, RotateCcw, CheckCircle2, XCircle } from 'lucide-react'
import { api } from '../services/api'
import { Flashcard } from '../types'
import LoadingSpinner from '../components/LoadingSpinner'

const RATINGS: Array<{
  quality: number
  label: string
  hint: string
  color: string
}> = [
  { quality: 1, label: 'De nuevo',  hint: 'No lo recordé',          color: '#EF4444' },
  { quality: 2, label: 'Difícil',   hint: 'Me costó mucho',         color: '#F97316' },
  { quality: 3, label: 'Bien',      hint: 'Con esfuerzo',           color: '#FBBF24' },
  { quality: 4, label: 'Fácil',     hint: 'Lo recordé rápido',      color: '#22C55E' },
  { quality: 5, label: 'Perfecto',  hint: 'Sin dudarlo',            color: '#10B981' },
]

export default function ReviewPage() {
  const navigate = useNavigate()
  const [queue, setQueue] = useState<Flashcard[]>([])
  const [currentIdx, setCurrentIdx] = useState(0)
  const [flipped, setFlipped] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [sessionStats, setSessionStats] = useState({ reviewed: 0, again: 0 })
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const current = queue[currentIdx]
  const remaining = queue.length - currentIdx

  const loadDue = useCallback(async () => {
    setIsLoading(true)
    setErrorMsg(null)
    try {
      const { items } = await api.getDueFlashcards(50)
      setQueue(items)
      setCurrentIdx(0)
      setFlipped(false)
      setSessionStats({ reviewed: 0, again: 0 })
    } catch (e) {
      setErrorMsg((e as Error).message || 'No se pudieron cargar las flashcards.')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => { loadDue() }, [loadDue])

  const handleRate = useCallback(
    async (quality: number) => {
      if (!current || isSubmitting) return
      setIsSubmitting(true)
      try {
        await api.reviewFlashcard(current.cardId, quality)
        setSessionStats((s) => ({
          reviewed: s.reviewed + 1,
          again: s.again + (quality < 3 ? 1 : 0),
        }))
        setFlipped(false)
        setCurrentIdx((i) => i + 1)
      } catch (e) {
        setErrorMsg((e as Error).message || 'Error guardando la respuesta.')
      } finally {
        setIsSubmitting(false)
      }
    },
    [current, isSubmitting],
  )

  // Keyboard shortcuts: space = flip, 1-5 = rate
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!current) return
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault()
        setFlipped((f) => !f)
        return
      }
      if (flipped && ['1', '2', '3', '4', '5'].includes(e.key)) {
        handleRate(parseInt(e.key, 10))
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [current, flipped, handleRate])

  const progressPct = useMemo(() => {
    if (queue.length === 0) return 0
    return Math.round((sessionStats.reviewed / queue.length) * 100)
  }, [queue.length, sessionStats.reviewed])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Cargando tu sesión de repaso..." />
      </div>
    )
  }

  if (!queue.length) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#F1F5F9' }}>Repaso</h1>
          <p className="text-sm mt-1" style={{ color: '#64748B' }}>
            Flashcards con repetición espaciada (SM-2).
          </p>
        </div>

        <div className="glass-card p-10 text-center">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{ background: 'linear-gradient(135deg, rgba(79,70,229,0.2), rgba(139,92,246,0.1))' }}
          >
            <Sparkles className="w-8 h-8" style={{ color: '#A5B4FC' }} />
          </div>
          <h2 className="text-lg font-semibold" style={{ color: '#F1F5F9' }}>
            Sin cartas pendientes
          </h2>
          <p className="text-sm mt-2 mb-6" style={{ color: '#94A3B8' }}>
            Genera flashcards desde un material o vuelve más tarde cuando tus cartas estén listas para repasar.
          </p>
          <button
            onClick={() => navigate('/materials')}
            className="px-4 py-2 rounded-xl text-sm font-semibold"
            style={{
              background: 'linear-gradient(135deg, #4F46E5, #8B5CF6)',
              color: 'white',
            }}
          >
            Ir a mis materiales
          </button>
        </div>
      </div>
    )
  }

  // Session finished
  if (!current) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="glass-card p-10 text-center">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.2), rgba(34,197,94,0.1))' }}
          >
            <CheckCircle2 className="w-8 h-8" style={{ color: '#10B981' }} />
          </div>
          <h2 className="text-lg font-semibold" style={{ color: '#F1F5F9' }}>
            ¡Sesión completada!
          </h2>
          <p className="text-sm mt-2" style={{ color: '#94A3B8' }}>
            Repasaste {sessionStats.reviewed} cartas. {sessionStats.again > 0
              ? `${sessionStats.again} volverán pronto.`
              : 'Todo bajo control.'}
          </p>

          <button
            onClick={loadDue}
            className="mt-6 px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2 mx-auto"
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#E2E8F0',
            }}
          >
            <RotateCcw className="w-4 h-4" />
            Buscar más cartas
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#F1F5F9' }}>Repaso</h1>
          <p className="text-sm mt-1" style={{ color: '#64748B' }}>
            {remaining} carta{remaining === 1 ? '' : 's'} pendiente{remaining === 1 ? '' : 's'}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-wide" style={{ color: '#475569' }}>
            Progreso
          </p>
          <p className="text-lg font-bold" style={{ color: '#A5B4FC' }}>{progressPct}%</p>
        </div>
      </div>

      {/* Progress bar */}
      <div
        className="h-1.5 rounded-full overflow-hidden"
        style={{ background: 'rgba(255,255,255,0.06)' }}
      >
        <div
          className="h-full transition-all duration-300"
          style={{
            width: `${progressPct}%`,
            background: 'linear-gradient(90deg, #4F46E5, #8B5CF6)',
          }}
        />
      </div>

      {errorMsg && (
        <div
          className="glass-card p-4 flex items-center gap-3"
          style={{ border: '1px solid rgba(239,68,68,0.3)' }}
        >
          <XCircle className="w-5 h-5 flex-shrink-0" style={{ color: '#EF4444' }} />
          <p className="text-sm" style={{ color: '#FCA5A5' }}>{errorMsg}</p>
        </div>
      )}

      {/* Card */}
      <button
        onClick={() => setFlipped((f) => !f)}
        className="w-full glass-card p-8 min-h-[260px] text-left transition-transform active:scale-[0.99]"
        style={{ cursor: 'pointer' }}
        aria-label="Voltear carta"
      >
        <div className="flex items-center justify-between mb-4">
          <span
            className="text-xs font-semibold uppercase tracking-wide px-2 py-1 rounded-md"
            style={{
              color: flipped ? '#86EFAC' : '#A5B4FC',
              background: flipped ? 'rgba(34,197,94,0.1)' : 'rgba(79,70,229,0.1)',
            }}
          >
            {flipped ? 'Respuesta' : 'Pregunta'}
          </span>
          <span className="text-xs" style={{ color: '#64748B' }}>
            {currentIdx + 1} / {queue.length}
          </span>
        </div>

        <p
          className="text-lg leading-relaxed whitespace-pre-wrap"
          style={{ color: '#F1F5F9' }}
        >
          {flipped ? current.back : current.front}
        </p>

        {!flipped && (
          <p className="text-xs mt-6" style={{ color: '#475569' }}>
            Toca la tarjeta o pulsa <kbd className="px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.08)' }}>Espacio</kbd> para ver la respuesta
          </p>
        )}
      </button>

      {/* Rating buttons — only visible when answer is shown */}
      {flipped && (
        <div className="grid grid-cols-5 gap-2">
          {RATINGS.map((r) => (
            <button
              key={r.quality}
              onClick={() => handleRate(r.quality)}
              disabled={isSubmitting}
              className="flex flex-col items-center gap-1 py-3 px-2 rounded-xl transition-all text-center disabled:opacity-40"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: `1px solid ${r.color}33`,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = `${r.color}15` }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)' }}
            >
              <span className="text-xs font-bold" style={{ color: r.color }}>{r.quality}</span>
              <span className="text-xs font-semibold" style={{ color: '#E2E8F0' }}>{r.label}</span>
              <span className="text-[10px] leading-tight" style={{ color: '#64748B' }}>{r.hint}</span>
            </button>
          ))}
        </div>
      )}

      <p className="text-xs text-center" style={{ color: '#475569' }}>
        Atajos: <kbd className="px-1.5 py-0.5 rounded mx-0.5" style={{ background: 'rgba(255,255,255,0.08)' }}>Espacio</kbd> voltear ·
        <kbd className="px-1.5 py-0.5 rounded mx-0.5" style={{ background: 'rgba(255,255,255,0.08)' }}>1-5</kbd> calificar
      </p>
    </div>
  )
}
